import { PAGE_PATH } from '@/constants';
import {
  fetchActiveOrderByRegularOrder,
  fetchCheckProductCanCheckout,
  fetchEligibleShippingMethodsByOutrightPurchase,
  fetchEligibleShippingMethodsByRegularOrder,
  fetchFreeGiftByOrderId,
  fetchGetOrderIsIncludeMemberPrice,
  fetchGetShoppingCart,
  fetchMarkUpByOrder,
  fetchModifyCartItemCountByShoppingTrolley,
  fetchOrderPromotionResult,
  fetchSetShippingMethodByOutrightPurchase,
  fetchSetShippingMethodByRegularOrder,
  fetchSettlementShoppingCart,
} from '@/services';
import { useAppStore, useCouponStore } from '@/store';
import { PlaceOrderType, PromotionType, VirtualTargetType, type AnyObjOrNull } from '@/types';
import type { Commodity, ShoppingTrolley } from '@/types/global-business';
import { businessUtils } from '@/utils';
import NP from 'number-precision';
import { defineStore } from 'pinia';
import { useAuthStore } from './auth';

interface State {
  /** 购物车、预下单卡片组数据 */
  shoppingTrolleyCardGroup: ShoppingTrolley.CardGroup[];
  /** 直接下单卡片组数据 */
  regularOrderCardGroup: ShoppingTrolley.CardGroup[];
  /** 购物车数据 */
  shoppingTrolley: AnyObjOrNull;
  /** 预下单数据 */
  outrightPurchase: AnyObjOrNull;
  /** 直接下单数据 */
  regularOrder: AnyObjOrNull;
  /** 购物车活动数据 */
  shoppingTrolleyPromotionResult: AnyObjOrNull;
  /** 预下单活动数据 */
  outrightPurchasePromotionResult: AnyObjOrNull;
  /** 直接下单活动数据 */
  regularOrderPromotionResult: AnyObjOrNull;
  /** 已售罄、失效的商品数据 */
  invalidCommodityList: ShoppingTrolley.Commodity[];
  /** 下单场景类型 */
  placeOrderType: PlaceOrderType;
  /** 当前订单的所有赠品 */
  freeGiftByOrder: AnyObject[];
  /** 当前订单的所有换购商品 */
  purchasePremiumByOrder: AnyObject[];
  /** 渲染的换购商品 */
  purchasePremiumCommodityList: AnyObject[];
  /** 优惠弹窗实例 */
  preferentailPopupInstance: AnyObjOrNull;
  /** 删除标识 */
  displayCardDelete: boolean;
  /** 选择的活动弹窗信息 */
  selectedCardGroupItem: ShoppingTrolley.CardGroup | null;
  // 已选择的购物车商品过滤掉定时开售的
  selectedCardNotWithPutOnSale: AnyObject[];
  /** 支付成功后的回调函数 */
  paySuccessCallback: Function | null;
  /** 下单留言 */
  leaveWordText: string;
  /** 下单地址 */
  shipAddress: AnyObjOrNull;
  /** 购买选项 */
  pruchaseOptions: {
    /** 规格 */
    sku: Commodity.SkuType | null;
    /** 数量 */
    count: number;
  };
  /** 订阅提交数据 */
  subscribesSubmitParams: {
    subscriptionPlanId: string;
    periods: number;
    deliveryInterval: number;
    firstShippingDate: string | Date;
  };
  /** 订单中的商品是否参与了会员价活动 */
  whetherOrderContainMemberPrice: boolean;
  /** 订单是否使用了会员优惠（是否勾选） */
  whetherOrderUseMemberDiscount: boolean;
  /** 订单是否使用了购物金 */
  whetherOrderUseShoppingCredit: boolean;
  /** 查询商品规格是否限购的结果数据 */
  canCheckoutResult: {
    canCheckout: boolean;
    reason: string;
  };
  regularSalePopupRef: AnyObject | null;
  /** 商品地区限制错误提示 */
  isProductRegionRestrictionError: boolean;
  /** 页面弹窗 */
  popupMap: {
    coupon: AnyObject | null;
  };
}

export const usePlaceOrderStore = defineStore('placeOrder', {
  state: (): State => ({
    shoppingTrolleyCardGroup: [],
    regularOrderCardGroup: [],
    shoppingTrolley: null,
    outrightPurchase: null,
    regularOrder: null,
    shoppingTrolleyPromotionResult: null,
    outrightPurchasePromotionResult: null,
    selectedCardNotWithPutOnSale: [],
    regularOrderPromotionResult: null,
    invalidCommodityList: [],
    freeGiftByOrder: [],
    purchasePremiumByOrder: [],
    purchasePremiumCommodityList: [],
    placeOrderType: PlaceOrderType.shoppingTrolley,
    preferentailPopupInstance: null,
    displayCardDelete: false,
    selectedCardGroupItem: null,
    paySuccessCallback: null,
    leaveWordText: '',
    shipAddress: null,
    pruchaseOptions: {
      sku: null,
      count: 1,
    },
    subscribesSubmitParams: {
      subscriptionPlanId: '',
      periods: 0,
      deliveryInterval: 0,
      firstShippingDate: '',
    },
    whetherOrderContainMemberPrice: false,
    whetherOrderUseMemberDiscount: true,
    whetherOrderUseShoppingCredit: true,
    canCheckoutResult: {
      canCheckout: true,
      reason: '',
    },
    regularSalePopupRef: null,
    isProductRegionRestrictionError: false,
    popupMap: {
      coupon: null,
    },
  }),
  getters: {
    // 购物车商品是否全是定时开售或者失效
    isPutOnSaleOrEnabled(state) {
      const shoppingOnSale = state.shoppingTrolleyCardGroup
        .map((item) => item.commodityList)
        .flat()
        .filter(
          (item: any) =>
            item?._source?.productVariant.product.customFields.putOnSaleType === 'scheduled' &&
            new Date(item?._source.productVariant.product.customFields.putOnSaleTime).getTime() >
              new Date().getTime()
        );
      if (
        shoppingOnSale.length + state.invalidCommodityList.length ==
        useAppStore().shoppingTrolleyCount
      ) {
        return true;
      } else {
        return false;
      }
    },
    isRegularOrder(state) {
      return state.placeOrderType === PlaceOrderType.regularOrder;
    },
    /** 下单数据 */
    placeOrders(state): AnyObjOrNull {
      return this.isRegularOrder ? state.regularOrder : state.outrightPurchase;
    },
    /** 下单活动数据 */
    placeOrderPromotionResult(state): AnyObjOrNull {
      return this.isRegularOrder
        ? state.regularOrderPromotionResult
        : state.outrightPurchasePromotionResult;
    },
    /** 卡片组数据 */
    placeOrderCardGroup(state): AnyObject[] {
      return this.isRegularOrder ? state.regularOrderCardGroup : state.shoppingTrolleyCardGroup;
    },
    /** 是否全选 */
    isCheckedAll(state) {
      return state.shoppingTrolleyCardGroup
        .map((it) => it.commodityList)
        .flat()
        .filter((it) => !it.isCurrentSkuSoltOut)
        .filter(
          (it) =>
            !(
              it._source?.productVariant?.product?.customFields?.putOnSaleType &&
              new Date(it._source?.productVariant?.product?.customFields?.putOnSaleTime).getTime() >
                new Date().getTime()
            )
        )
        .every((it) => it.isChecked);
    },
    /** 已选择的商品数据 */
    selectedCommodityList(state): ShoppingTrolley.Commodity[] {
      const group = this.isRegularOrder
        ? state.regularOrderCardGroup
        : state.shoppingTrolleyCardGroup;

      return group
        .map((it) => it.commodityList)
        .flat()
        .filter((it) => it.isChecked);
    },
    /** 优惠类型详情 */
    discountByTypesList(): AnyObject[] {
      return businessUtils.placeOrder.getDiscounts(
        this.placeOrderPromotionResult?.promResult?.discountByTypes ?? []
      );
    },
    /** 购物金数据 */
    shoppingGold(): {
      total: number;
      shoppingCreditsDeduction: number;
      shoppingCreditsClaim: number;
      disableShoppingCredits: boolean;
    } {
      const promResult = this.placeOrderPromotionResult?.promResult;
      // 购物金抵扣
      const shoppingCreditsDeduction = promResult?.shoppingCreditsDeduction ?? 0;
      // 可得购物金
      const shoppingCreditsClaim = promResult?.shoppingCreditsClaim ?? 0;
      // 总金额
      const total = NP.plus(promResult?.orderTotalPrice ?? 0, shoppingCreditsDeduction);
      // 是否禁用购物金
      const disableShoppingCredits = promResult?.disableShoppingCredits ?? false;

      console.log(`购物金数据`, {
        total,
        shoppingCreditsClaim,
        shoppingCreditsDeduction,
        disableShoppingCredits,
      });

      return {
        total,
        shoppingCreditsClaim,
        shoppingCreditsDeduction,
        disableShoppingCredits,
      };
    },
    /** 实付/合计 */
    totalActualPayment(): number {
      return this.placeOrders?.customFields?.orderPromotionResult?.promResult?.orderTotalPrice ?? 0;
    },
    /** 结算商品是否积分兑换优惠券商品 */
    isVirtualCouponType(): boolean {
      const item = this.selectedCommodityList[0];
      console.log(`isVirtualMemberType`, item);
      if (!item) return false;
      return (
        item._source.productVariant.customFields.virtualTargetType === VirtualTargetType.Coupon &&
        item.isPointsExchange
      );
    },
  },
  actions: {
    /**
     * 修改购物车数量
     * @param orderLineId
     * @param count
     */
    async modifyShoppingTrolleyCount(orderLineId: string, count: number, callback?: () => void) {
      const res = await fetchModifyCartItemCountByShoppingTrolley(orderLineId, count);
      if (res) {
        callback?.();
        await this.exeShoppingTrolleyFlow();
        return res;
      }
    },
    /**
     * 修改分组勾选
     * @param index - 分组下标
     */
    async changeShoppingTrolleyCardGroupChecked(index: number) {
      const target = this.shoppingTrolleyCardGroup[index];

      if (target.isChecked) {
        // 已勾选
        target.commodityList.forEach((it) => (it.isChecked = false));
      } else {
        // 未勾选
        target.commodityList.forEach((it) => (it.isChecked = true));
      }

      // 反选自己
      target.isChecked = !target.isChecked;

      if (!this.displayCardDelete) {
        this.checkProductCanCheckout();
        return await this.exeShoppingTrolleyFlow();
      }
    },
    /**
     * 修改商品项勾选
     * @param index - 分组下标
     * @param cindex - 商品下标
     */
    async changeShoppingTrolleyCommodityItem(index: number, cindex: number) {
      const group = this.shoppingTrolleyCardGroup[index];
      const target = group.commodityList[cindex];

      // 反选
      target.isChecked = !target.isChecked;

      // 当前是否分组
      if (group.isGroup) {
        // 分组下的商品是否已经全选
        const isGroupChecked = group.commodityList.every((it) => it.isChecked);
        group.isChecked = isGroupChecked;
      }

      if (!this.displayCardDelete) {
        this.checkProductCanCheckout();
        return await this.exeShoppingTrolleyFlow();
      }
    },
    /** 全选 */
    async checkedAll() {
      this.shoppingTrolleyCardGroup.forEach((it) => {
        it.commodityList.forEach((cit) => {
          if (!cit.isCurrentSkuSoltOut) cit.isChecked = true;
        });
        it.isChecked = it.commodityList.every((it) => !it.isCurrentSkuSoltOut);
      });

      if (!this.displayCardDelete) {
        this.checkProductCanCheckout();
        return await this.exeShoppingTrolleyFlow();
      }
    },
    /** 取消全选 */
    async cancelCheckedAll() {
      this.shoppingTrolleyCardGroup.forEach((it) => {
        it.commodityList.forEach((cit) => {
          if (!cit.isCurrentSkuSoltOut) cit.isChecked = false;
        });

        // 管理场景下，反选需要把卡片组的勾选也去掉
        if (this.displayCardDelete) {
          it.isChecked = false;
        } else {
          it.isChecked = it.commodityList.every(
            (it) =>
              !it.isCurrentSkuSoltOut &&
              !(
                new Date(
                  it._source?.productVariant.product?.customFields?.putOnSaleTime
                ).getTime() > new Date().getTime() &&
                it._source?.productVariant.product?.customFields?.putOnSaleType == 'scheduled'
              )
          );
        }
      });

      if (!this.displayCardDelete) {
        this.checkProductCanCheckout();
        return await this.exeShoppingTrolleyFlow();
      }
    },
    /**
     * 查询商品规格是否限购
     * @returns
     */
    async checkProductCanCheckout() {
      try {
        const skuIds = this.selectedCommodityList
          .filter((it) => {
            const disabledItem =
              it.isCurrentSkuSoltOut ||
              !it.isCurrentSkuExist ||
              it.isTimedSale ||
              it.isProductPurchasePermission;
            if (!disabledItem) return it;
          })
          .map((it) => it.skuId);

        const res = await fetchCheckProductCanCheckout(skuIds);

        if (res) this.canCheckoutResult = res;

        return res;
      } catch (error) {
        console.log(error);
      }
    },
    async exeShoppingTrolleyFlow(isMount: boolean = false, cartSetlment?: AnyObjOrNull[]) {
      if (!isMount) {
        const group = this.shoppingTrolleyCardGroup
          .map((it) => it.commodityList)
          .flat()
          .filter((it) => it.isChecked);

        // 根据本地已勾选状态重新丢进预下单
        const input = group.map((it) => ({
          productVariantId: it._source.productVariant.id,
          quantity: it.count,
        }));

        const input2: { productVariantId: any; quantity: any }[] = [];
        if (cartSetlment && cartSetlment?.length) {
          cartSetlment.forEach((item: AnyObjOrNull) => {
            group.forEach((item2: AnyObject) => {
              if (item?.productVariant.id === item2._source.productVariant.id) {
                input2.push({
                  productVariantId: item2._source.productVariant.id,
                  quantity: item?.quantity,
                });
              }
            });
          });

          // 进行结算
          await fetchSettlementShoppingCart(input2);
        } else {
          // 进行结算
          await fetchSettlementShoppingCart(input);
        }
      }

      // 获取数据，执行数据和界面更新
      const data = await this.getShoppingTrolley(true);

      return data;
    },
    async getShoppingTrolley(refresh?: boolean) {
      // 没有登录不调用获取数据
      if (!useAuthStore().isLogin) return;

      if (!this.shoppingTrolleyCardGroup.length || refresh) {
        const getRes = await fetchGetShoppingCart(
          false,
          this.whetherOrderUseMemberDiscount,
          this.whetherOrderUseShoppingCredit
        );
        if (getRes) {
          const shoppingTrolleyId = getRes.shoppingTrolley.id;
          const outrightPurchaseId = getRes.outrightPurchase.id;

          // 更新角标数量
          useAppStore().shoppingTrolleyCount = getRes.shoppingTrolley.lines.length;

          // 获取活动信息
          const promotionResult = await fetchOrderPromotionResult([
            shoppingTrolleyId,
            outrightPurchaseId,
          ]);

          const shoppingTrolleyPromotionResult = promotionResult?.find(
            (it: AnyObject) => it.promResult?.orderId === shoppingTrolleyId
          );
          const outrightPurchasePromotionResult = promotionResult?.find(
            (it: AnyObject) => it.promResult?.orderId === outrightPurchaseId
          );

          console.log(`活动数据`, {
            shoppingTrolley: getRes.shoppingTrolley,
            shoppingTrolleyPromotionResult,
            outrightPurchasePromotionResult,
          });

          this.shoppingTrolley = getRes.shoppingTrolley;
          this.outrightPurchase = getRes.outrightPurchase;

          this.shoppingTrolleyPromotionResult = shoppingTrolleyPromotionResult;
          this.outrightPurchasePromotionResult = outrightPurchasePromotionResult;

          // 获取赠品
          await this.getFreeGiftByOrder(shoppingTrolleyId, true);
          // 获取换购
          await this.getMarkUpByOrder(shoppingTrolleyId, true);

          const shoppingTrolleyCardGroup = await businessUtils.placeOrder.getCartGroupCardConfig(
            getRes.shoppingTrolley,
            shoppingTrolleyPromotionResult,
            outrightPurchasePromotionResult,
            // 传递赠品和换购数据
            {
              freeGiftByOrder: this.freeGiftByOrder,
              purchasePremiumByOrder: this.purchasePremiumByOrder,
            }
          );

          this.shoppingTrolleyCardGroup = shoppingTrolleyCardGroup.filter(
            (it) => it.isGroup === null || it.isGroup
          );

          this.invalidCommodityList = shoppingTrolleyCardGroup.map((it) => it.invalidList).flat();
          if (this.invalidCommodityList.length) {
            console.log(`getShoppingTrolley - 失效商品数据`, this.invalidCommodityList);
          }

          console.log(`商品卡片组数据（过滤group）`, [...this.shoppingTrolleyCardGroup]);
          // console.log(`商品卡片组数据（没有过滤group）`, [...shoppingTrolleyCardGroup]);
        }
      }

      return {
        shoppingTrolleyCardGroup: this.shoppingTrolleyCardGroup,
      };
    },
    /** 获取直接下单的订单信息 */
    async getRegularOrder(refresh: boolean = false) {
      // 没有登录，不调用直接下单活跃订单
      if (!useAuthStore().isLogin) return;

      if (!this.regularOrder || refresh) {
        const regularOrder = await fetchActiveOrderByRegularOrder();
        if (regularOrder) this.regularOrder = regularOrder;

        // 获取活动信息
        const promotionResult = await fetchOrderPromotionResult([regularOrder.id]);

        // 获取赠品
        await this.getFreeGiftByOrder(regularOrder.id, true);
        // 获取换购
        await this.getMarkUpByOrder(regularOrder.id, true);

        const regularOrderPromotionResult = promotionResult[0];

        this.regularOrderPromotionResult = regularOrderPromotionResult;

        const regularOrderCardGroup = await businessUtils.placeOrder.getCartGroupCardConfig(
          regularOrder,
          regularOrderPromotionResult,
          regularOrderPromotionResult,
          {
            freeGiftByOrder: this.freeGiftByOrder,
            purchasePremiumByOrder: this.purchasePremiumByOrder,
          }
        );

        this.regularOrderCardGroup = regularOrderCardGroup.filter(
          (it) => it.isGroup === null || it.isGroup
        );
      }

      return this.regularOrder;
    },
    async getFreeGiftByOrder(orderId: string, refresh?: boolean) {
      if (!this.freeGiftByOrder.length || refresh) {
        const data = await fetchFreeGiftByOrderId(orderId);
        if (data) this.freeGiftByOrder = data;
      }
    },
    async getMarkUpByOrder(orderId: string, refresh?: boolean) {
      if (!this.purchasePremiumByOrder.length || refresh) {
        const data = await fetchMarkUpByOrder(orderId);
        if (data) {
          let list: AnyObject[] = [];
          data.forEach((it: AnyObject) => {
            it.purchasePremiumProducts.forEach((pp: AnyObject) => list.push(pp));
          });
          this.purchasePremiumByOrder = list;
        }
      }
    },
    /** 获取优惠商品选择器的配置信息 */
    getPreferentialCommoditySelectorConfig(data: AnyObject) {
      const config = {
        title: '',
        subTitleOptions: {
          text: '',
          color: '',
          isAnimation: false,
        },
        scene: '',
        visible: false,
        canUnselect: false,
      };

      if (data.type === PromotionType.purchaseAtAPremium) {
        config.title = '优惠换购';
        config.subTitleOptions = { text: '勾选换购', color: '#ef6348', isAnimation: true };
        config.scene = 'purchasePremium';
        config.visible = true;
        config.canUnselect = true;
      } else if (
        [
          PromotionType.selectiveGift,
          PromotionType.fullDiscountPresent,
          PromotionType.actuallyPaid,
        ].includes(data.type)
      ) {
        config.title =
          data.type === PromotionType.selectiveGift
            ? '任选满赠'
            : data.type === PromotionType.actuallyPaid
            ? '实付满赠'
            : '满减送赠品';
        config.subTitleOptions = { text: '以下赠品无需付费', color: '#999897', isAnimation: false };
        config.scene = 'freeGift';

        // 根据活动类型判断是否渲染
        const promotionTypeToFlag = {
          [PromotionType.selectiveGift]:
            this.shoppingTrolleyPromotionResult?.isCartShowSelectiveGift,
          [PromotionType.fullDiscountPresent]:
            this.shoppingTrolleyPromotionResult?.isCartShowFullDiscountPresentGift,
          [PromotionType.actuallyPaid]:
            this.shoppingTrolleyPromotionResult?.isCartShowActuallyPaidGift,
        } as Record<PromotionType, boolean>;

        // 判断是否是订单确认页
        const pages = getCurrentPages();
        const currentPage = pages[pages.length - 1];
        const isOrderConfirmPage = `/${currentPage.route}` === PAGE_PATH.orderConfirm.route;

        // 如果是订单确认页，必需渲染；否则根据活动配置判断是否渲染
        config.visible = isOrderConfirmPage || promotionTypeToFlag[data.type as PromotionType];
      }

      return config;
    },
    /** 设置物流信息 */
    async setShippingMethods() {
      if (this.placeOrderType === PlaceOrderType.regularOrder) {
        const shipMethods = await fetchEligibleShippingMethodsByRegularOrder();
        await fetchSetShippingMethodByRegularOrder(shipMethods?.[0].id);
      } else {
        const shipMethods = await fetchEligibleShippingMethodsByOutrightPurchase();
        await fetchSetShippingMethodByOutrightPurchase(shipMethods?.[0].id);
      }
    },
    /** 更新下单操作 - 优惠信息更变时重跑一遍预下单流程更新全部优惠信息 */
    async updateHandler() {
      // 设置物流信息
      if (this.isRegularOrder) {
        const shipMethods = await fetchEligibleShippingMethodsByRegularOrder();
        await fetchSetShippingMethodByRegularOrder(shipMethods?.[0].id);
      } else {
        const shipMethods = await fetchEligibleShippingMethodsByOutrightPurchase();
        await fetchSetShippingMethodByOutrightPurchase(shipMethods?.[0].id);
      }

      // 通用操作
      const actions = async (orderId: string) => {
        // 获取是否包含会员价
        if (this.placeOrders?.id) {
          this.whetherOrderContainMemberPrice = await fetchGetOrderIsIncludeMemberPrice(
            this.placeOrders.id
          );
        }

        // 创建新的支付订单时 清空下单时的留言缓存
        this.leaveWordText = '';

        // 获取优惠券信息
        await useCouponStore().getUserReceiveCouponList(orderId, true);
      };

      // 获取订单信息
      if (this.isRegularOrder) {
        const regularOrder = await this.getRegularOrder(true);
        await actions(regularOrder?.id);
      } else {
        await this.getShoppingTrolley(true);
        await actions(this.outrightPurchase?.id);
      }
    },
    /** 重置操作 */
    async resetHandler() {
      // 重置下单类型
      this.placeOrderType = PlaceOrderType.shoppingTrolley;

      // 清空直接下单数据
      this.regularOrder = null;
      this.regularOrderPromotionResult = null;
      this.regularOrderCardGroup = [];

      this.isProductRegionRestrictionError = false;

      // 清空购物车和预下单
      // ...

      // 更新数据
      this.getShoppingTrolley(true);
    },
    resetPruchaseOptions() {
      this.pruchaseOptions = {
        sku: null,
        count: 1,
      };
    },
  },
});
